import {
  Component,
  ComponentRef,
  EnvironmentInjector,
  Injector,
  Type,
  effect,
  inject,
  viewChild,
  ViewContainerRef
} from "@angular/core";
import { SIDE_PANEL_DATA } from "../../constants/side-panel-data.token";
import { SidePanelRef } from "../../models/side-panel/side-panel-ref";
import { SidePanelState } from "../../models/side-panel/side-panel-open-options.model";
import { SidePanelService } from "../../services/side-panel.service";

@Component({
  selector: "app-side-panel",
  templateUrl: "./side-panel.html",
  styleUrl: "./side-panel.scss"
})
export class SidePanel {
  protected readonly sidePanelService = inject(SidePanelService);

  private readonly injector = inject(Injector);
  private readonly environmentInjector = inject(EnvironmentInjector);
  private readonly contentHost = viewChild.required("contentHost", { read: ViewContainerRef });

  private componentRef: ComponentRef<unknown> | null = null;

  constructor() {
    effect(() => {
      const isOpen = this.sidePanelService.isOpen();
      const config = this.sidePanelService.config();

      if (isOpen && config) {
        queueMicrotask(() => this.renderComponent(config));
        return;
      }

      this.destroyComponent();
    });
  }

  onBackdropClick(): void {
    this.sidePanelService.close();
  }

  onCloseClick(): void {
    this.sidePanelService.close();
  }

  onPanelKeydown(event: KeyboardEvent): void {
    if (event.key === "Escape") {
      this.sidePanelService.close();
    }
  }

  private renderComponent(config: SidePanelState): void {
    const host = this.contentHost();
    if (!host) {
      return;
    }

    this.destroyComponent();

    const sidePanelRef = new SidePanelRef(() => this.sidePanelService.close());
    const childInjector = Injector.create({
      providers: [
        { provide: SIDE_PANEL_DATA, useValue: config.data },
        { provide: SidePanelRef, useValue: sidePanelRef }
      ],
      parent: this.injector
    });

    this.componentRef = host.createComponent(config.component as Type<unknown>, {
      environmentInjector: this.environmentInjector,
      injector: childInjector
    });
  }

  private destroyComponent(): void {
    this.componentRef?.destroy();
    this.componentRef = null;
    this.contentHost()?.clear();
  }
}
